import React, { Component, createContext, ReactNode, useContext, useEffect } from 'react';

import { AuthContext } from '@contexts/AuthContext';

import Logger from '@util/Logger';

const logger = Logger.make('BaseWebSocketProvider');

enum ErrorType {
    Auth = 'auth',
    WebSocket = 'websocket',
    Message = 'message',
}

export type WebSocketEventHandler = (data: any) => void;

export interface WebSocketMessage {
    type: string;
    [key: string]: any;
}

export interface WebSocketContextValue {
    socket: WebSocket | null;
    connected: boolean;
    subscribe: (eventType: string, handler: WebSocketEventHandler) => () => void;
    unsubscribe: (eventType: string, handler: WebSocketEventHandler) => void;
}

// Create a WeakMap to store class-specific contexts
const classContexts = new WeakMap<typeof BaseWebSocketProvider, React.Context<WebSocketContextValue | null>>();

export interface BaseWebSocketProviderProps {
    children: ReactNode;
    url: string;
    getAuthPayload: (accessToken: string, params?: object) => object;
    params?: object;
    loggerName?: string;
    onMessage?: (message: WebSocketMessage) => void;
    onAuthSuccess?: () => void;
    onAuthFailure?: (error?: any) => void;
    onError?: (error: Event) => void;
}

interface BaseWebSocketProviderState {
    connected: boolean;
}

export class BaseWebSocketProvider extends Component<BaseWebSocketProviderProps, BaseWebSocketProviderState> {
    protected validateParams(): boolean {
        return true;
    }

    static contextType = AuthContext;
    declare context: React.ContextType<typeof AuthContext>;

    protected socket: WebSocket | null = null;
    private eventHandlers: Map<string, Set<WebSocketEventHandler>> = new Map();
    private isAuthenticated = false;
    private lastAuthAttempt = 0;

    state = {
        connected: false,
    };

    static getContext(providerClass: typeof BaseWebSocketProvider) {
        if (!classContexts.has(providerClass)) {
            classContexts.set(providerClass, createContext<WebSocketContextValue | null>(null));
        }
        return classContexts.get(providerClass)!;
    }

    protected subscribe = (eventType: string, handler: WebSocketEventHandler) => {
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, new Set());
        }
        this.eventHandlers.get(eventType)?.add(handler);
        return () => this.unsubscribe(eventType, handler);
    };

    protected unsubscribe = (eventType: string, handler: WebSocketEventHandler) => {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
                this.eventHandlers.delete(eventType);
            }
        }
    };

    private handleError = (error: any, type: ErrorType) => {
        const prefix = this.props.loggerName || 'BaseWebSocket';
        switch (type) {
            case ErrorType.Auth:
                logger.error(`[${prefix}] WebSocket authentication failed:`, error);
                this.props.onAuthFailure?.(error);
                break;
            case ErrorType.WebSocket:
                logger.error(`[${prefix}] WebSocket error occurred:`, error);
                this.props.onError?.(error);
                break;
            case ErrorType.Message:
                logger.error(`[${prefix}] Error processing WebSocket message:`, error);
                break;
        }
    };

    private cleanup = (ws?: WebSocket | null) => {
        // Only cleanup if the socket passed matches the current socket or if no socket was passed
        if (!ws || ws === this.socket) {
            if (this.socket?.readyState !== WebSocket.CLOSED) {
                this.socket?.close();
            }
            this.socket = null;
            this.isAuthenticated = false;
            this.setState({ connected: false });
        }
    };

    protected handleMessage = (message: WebSocketMessage) => {
        const prefix = this.props.loggerName || 'BaseWebSocket';
        logger.debug(`[${prefix}] Received message:`, message);
        this.props.onMessage?.(message);

        const { type } = message;
        this.eventHandlers.get(type)?.forEach((handler) => {
            try {
                handler(message);
            } catch (error) {
                logger.error(`Error in handler for event type ${type}:`, error);
            }
        });
    };

    private setupWebSocket = (accessToken: string) => {
        const { url, getAuthPayload, params } = this.props;

        if (!url || !accessToken) {
            logger.debug('Cannot setup WebSocket: missing url or access token');
            return;
        }

        // Never close an authenticated connection
        if (this.socket?.readyState === WebSocket.OPEN) {
            if (this.isAuthenticated) {
                return;
            }
        }

        // Only create a new socket if we don't have one or if current one is closed
        if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
            return;
        }

        const ws = new WebSocket(url);
        this.socket = ws;
        this.isAuthenticated = false;
        this.lastAuthAttempt = Date.now();

        ws.onopen = () => {
            const authPayload = JSON.stringify(getAuthPayload(accessToken, params));
            logger.info('WebSocket opened, sending auth');
            ws.send(authPayload);
        };

        ws.onmessage = (event) => {
            try {
                const message: WebSocketMessage = JSON.parse(event.data);

                if (message.type === 'auth-success') {
                    const prefix = this.props.loggerName || 'BaseWebSocket';
                    logger.info(`[${prefix}] WebSocket authenticated successfully`);
                    this.isAuthenticated = true;
                    this.setState({ connected: true });
                    this.props.onAuthSuccess?.();
                    return;
                }
                if (message.type === 'auth-failure') {
                    this.handleError(message, ErrorType.Auth);
                    this.cleanup(ws);
                    return;
                }

                this.handleMessage(message);
            } catch (error) {
                this.handleError(error, ErrorType.Message);
                this.cleanup();
            }
        };

        ws.onclose = (event) => {
            logger.info(`WebSocket closed (${event.wasClean ? 'clean' : 'unclean'}, code ${event.code})`);
            this.cleanup(ws);
        };

        ws.onerror = (error) => this.handleError(error, ErrorType.WebSocket);
    };

    private WebSocketManager = () => {
        const { getTokens, ready: authReady } = this.context;

        useEffect(() => {
            if (!this.props.url || !authReady) {
                logger.debug('WebSocket manager not ready: missing url or auth not ready');
                return;
            }

            const initializeConnection = () => {
                const tokens = getTokens();
                if (!tokens?.access_token) {
                    logger.debug('WebSocket manager waiting for access token');
                    return;
                }

                if (!this.validateParams()) {
                    logger.debug('WebSocket manager waiting for required params');
                    return;
                }

                // Only proceed if we're not already attempting to connect
                if (Date.now() - this.lastAuthAttempt < 5000 && this.socket?.readyState === WebSocket.CONNECTING) {
                    return;
                }

                // Close existing socket if we have new params
                if (this.socket?.readyState === WebSocket.OPEN) {
                    this.cleanup(this.socket);
                }

                this.setupWebSocket(tokens.access_token);
            };

            initializeConnection();

            return () => {
                if (this.socket) {
                    this.cleanup(this.socket);
                }
            };
        }, [authReady, this.props.params]);

        return null;
    };

    static useWebSocket(providerClass: typeof BaseWebSocketProvider) {
        const context = useContext(BaseWebSocketProvider.getContext(providerClass));
        if (!context) {
            throw new Error('useWebSocket must be used within its WebSocketProvider');
        }
        return context;
    }

    render() {
        const contextValue: WebSocketContextValue = {
            socket: this.socket,
            connected: this.state.connected,
            subscribe: this.subscribe,
            unsubscribe: this.unsubscribe,
        };

        const Context = BaseWebSocketProvider.getContext(this.constructor as typeof BaseWebSocketProvider);

        return (
            <Context.Provider value={contextValue}>
                <this.WebSocketManager />
                {this.props.children}
            </Context.Provider>
        );
    }
}

export default BaseWebSocketProvider;
